import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const DEFAULT_DAYS = 30;

function isoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export interface WritingQualityFilters {
  from: string;
  to: string;
  writer: string;
  action: string;
}

/** Filters live in the URL so they survive navigating to a writer and back. */
export function useWritingQualityFilters() {
  const [params, setParams] = useSearchParams();
  const today = new Date();
  const filters: WritingQualityFilters = {
    from: params.get("from") ?? isoDay(new Date(today.getTime() - DEFAULT_DAYS * 86_400_000)),
    to: params.get("to") ?? isoDay(today),
    writer: params.get("writer") ?? "",
    action: params.get("action") ?? "",
  };
  const setFilter = (key: keyof WritingQualityFilters, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  return { filters, setFilter };
}

/** Query string carrying the date and action filters to another page. */
export function carryFilters(filters: WritingQualityFilters): string {
  const qs = new URLSearchParams({ from: filters.from, to: filters.to });
  if (filters.action) qs.set("action", filters.action);
  return qs.toString();
}

export function useEvaluatedArticles(filters: WritingQualityFilters) {
  return useQuery({
    queryKey: ["writing-quality", "articles", filters.from, filters.to],
    queryFn: () => api.writingQuality.articles({ from: filters.from, to: filters.to }),
    staleTime: 5 * 60_000,
  });
}

export function formatScore(value: number | null): string {
  return value === null ? "—" : value.toFixed(1);
}

export function formatDate(iso: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export type SortDir = "asc" | "desc";

export function useSort<T>(rows: T[], initialKey: keyof T, initialDir: SortDir) {
  const [sort, setSort] = useState<{ key: keyof T; dir: SortDir }>({ key: initialKey, dir: initialDir });
  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const x = a[sort.key];
      const y = b[sort.key];
      const cmp =
        typeof x === "number" && typeof y === "number"
          ? x - y
          : String(x).localeCompare(String(y));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort]);
  const toggle = (key: keyof T) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }
    );
  return { sorted, sort, toggle };
}
